import React, { useState, useEffect } from 'react';
import { Flex, Checkbox, Tooltip, message } from "antd";
import { LeftOutlined, RightOutlined, SyncOutlined, AppstoreFilled, PieChartFilled } from "@ant-design/icons";
import { clrs } from "../../../constants/colors";
import IntlMessage from "../../../components/IntlMessage/IntlMessage";
import HorizontalDivider from "../../../components/Divider/HorizontalDivider";
import EmissionService from "../../../services/EmissionService";
import BlockLoading from "../../../components/LoadingComponents/BlockLoading";
import MonthString from "./MonthString";
import DataTable from "./DataTable";
import AnalyticsView from "./AnalyticsView";

const ArchiveView = () => {
    const [month, setMonth] = useState(12);
    const [year, setYear] = useState(2023);
    const [avgType, setAvgType] = useState('HOUR');

    const [emissions, setEmissions] = useState([]);
    const [checkedEmissions, setCheckedEmissions] = useState([]);
    const checkAll = emissions.length === checkedEmissions.length;
    const indeterminate = checkedEmissions.length > 0 && checkedEmissions.length < emissions.length;

    const [trends, setTrends] = useState([]);
    const [trendsAnalytics, setTrendsAnalytics] = useState([]);
    const [trendsAnalyticsMonth, setTrendsAnalyticsMonth] = useState([]);

    const [loading, setLoading] = useState(false);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);

    function isLeftMonthDisabled() {
        return month === 1;
    }

    function isRightMonthDisabled() {
        return month === 12;
    }

    function isLeftYearDisabled() {
        return year === 2023
    }

    function isRightYearDisabled() {
        return year === 2024
    }

    function isLeftAvgTypeDisabled() {
        return avgType === 'MINUTE'
    }

    function isRightAvgTypeDisabled() {
        return avgType === 'HOUR'
    }

    function getCurrentMonthString(month, year) {
        return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-01T00:00:00`;
    }

    function getNextMonthString(month, year) {
        const nextMonth = month === 12 ? 1 : month + 1;
        const nextYear = month === 12 ? year + 1 : year;
        return `${nextYear.toString().padStart(4, '0')}-${nextMonth.toString().padStart(2, '0')}-01T00:00:00`;
    }

    async function handleGetTrends() {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        setLoading(true);
        let arrTrends = [];
        for (const emissionName of checkedEmissions) {
            await EmissionService.getTrends(
                emissionName,
                getCurrentMonthString(month, year),
                getNextMonthString(month, year),
                avgType
            )
                .then((r) => {
                    arrTrends.push(...r.data);
                })
                .catch((r) => {
                    message.error(<IntlMessage id={'error'}/>, 5);
                })
        }
        setTrends(arrTrends);
        message.destroy();
        message.success(<IntlMessage id={'success'}/>, 5);
        setLoading(false);
    }

    async function handleGetTrendsAnalytics() {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        setLoadingAnalytics(true);
        let arrTrends = [];
        let arrTrendsMonth = [];
        for (const emissionName of checkedEmissions) {
            await EmissionService.getTrends(
                emissionName,
                getCurrentMonthString(month, year),
                getNextMonthString(month, year),
                'DAY'
            )
                .then((r) => {
                    arrTrends.push(...r.data);
                })
                .catch((r) => {
                    message.error(<IntlMessage id={'error'}/>, 5);
                })
            await EmissionService.getTrends(
                emissionName,
                getCurrentMonthString(month, year),
                getNextMonthString(month, year),
                'MONTH'
            )
                .then((r) => {
                    arrTrendsMonth.push(...r.data);
                })
                .catch((r) => {
                    message.error(<IntlMessage id={'error'}/>, 5);
                })
        }
        setTrendsAnalytics(arrTrends);
        setTrendsAnalyticsMonth(arrTrendsMonth);
        message.destroy();
        message.success(<IntlMessage id={'success'}/>, 5);
        setLoadingAnalytics(false);
    }

    useEffect(() => {
        if (showAnalytics) {
            handleGetTrendsAnalytics();
        }
    }, [showAnalytics]);

    useEffect(() => {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        EmissionService.getEmissions()
            .then((r) => {
                let arr = [];
                r.data.forEach(e => {
                    arr.push({
                        label: <IntlMessage id={`emission.${e.name}`}/>,
                        value: e.name
                    })
                })
                setEmissions(arr);
                setCheckedEmissions(arr.map(e => (e.value)));
                message.destroy();
            })
            .catch((result) => {
                message.destroy();
                message.error(<IntlMessage id={'error'}/>, 5);
            })
    }, []);

    useEffect(() => {
        if (emissions.length === checkedEmissions.length && emissions.length !== 0 && checkedEmissions !== 0) {
            handleGetTrends();
        }
    }, [checkedEmissions, emissions]);

    return (
        <>
            <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={15} wrap={"wrap"}>
                <Checkbox
                    indeterminate={indeterminate}
                    onChange={(e) => {
                        setCheckedEmissions(e.target.checked ? emissions.map(e => (e.value)) : []);
                    }}
                    checked={checkAll}
                >
                    <IntlMessage id={'all'}/>
                </Checkbox>
                <HorizontalDivider/>
                <Checkbox.Group
                    options={emissions} style={{gap: 10}}
                    value={checkedEmissions}
                    onChange={(e) => {setCheckedEmissions(e)}}
                />
            </Flex>

            <Flex style={{width: "100%"}} align={"flex-start"} justify={"flex-start"} gap={15} wrap={"wrap"}>
                <Flex gap={30} align={"center"} justify={"center"}
                      style={{
                          padding: 15,
                          backgroundColor: clrs.whiter,
                          borderRadius: 10,
                          borderBottom: `3px solid ${clrs.whiterBlacker}`
                      }}>
                    <LeftOutlined
                        onClick={() => {
                            if (!isLeftMonthDisabled()) {
                                setMonth(month - 1)
                            }
                        }}
                        style={{
                            color: isLeftMonthDisabled() ? clrs.whiterBlacker2 : clrs.black,
                            fontSize: "1.3rem",
                            cursor: isLeftMonthDisabled() ? "default" : "pointer"
                        }}/>
                    <span style={{fontWeight: 700, width: 100, textAlign: "center"}}>
                        <MonthString month={month}/>
                    </span>
                    <RightOutlined
                        onClick={() => {
                            if (!isRightMonthDisabled()) {
                                setMonth(month + 1)
                            }
                        }}
                        style={{
                            color: isRightMonthDisabled() ? clrs.whiterBlacker2 : clrs.black,
                            fontSize: "1.3rem",
                            cursor: isRightMonthDisabled() ? "default" : "pointer"
                        }}/>
                </Flex>

                <Flex gap={30} align={"center"} justify={"center"}
                      style={{
                          padding: 15,
                          backgroundColor: clrs.whiter,
                          borderRadius: 10,
                          borderBottom: `3px solid ${clrs.whiterBlacker}`
                      }}>
                    <LeftOutlined
                        onClick={() => {
                            if (!isLeftYearDisabled()) {
                                setYear(year - 1)
                            }
                        }}
                        style={{
                            color: isLeftYearDisabled() ? clrs.whiterBlacker2 : clrs.black,
                            fontSize: "1.3rem",
                            cursor: isLeftYearDisabled() ? "default" : "pointer"
                        }}/>
                    <span style={{fontWeight: 700, width: 100, textAlign: "center"}}>{year}</span>
                    <RightOutlined
                        onClick={() => {
                            if (!isRightYearDisabled()) {
                                setYear(year + 1)
                            }
                        }}
                        style={{
                            color: isRightYearDisabled() ? clrs.whiterBlacker2 : clrs.black,
                            fontSize: "1.3rem",
                            cursor: isRightYearDisabled() ? "default" : "pointer"
                        }}/>
                </Flex>

                {!showAnalytics &&
                    <Flex gap={30} align={"center"} justify={"center"}
                          style={{
                              padding: 15,
                              backgroundColor: clrs.whiter,
                              borderRadius: 10,
                              borderBottom: `3px solid ${clrs.whiterBlacker}`
                          }}>
                        <LeftOutlined
                            onClick={() => {
                                if (!isLeftAvgTypeDisabled()) {
                                    setAvgType('MINUTE')
                                }
                            }}
                            style={{
                                color: isLeftAvgTypeDisabled() ? clrs.whiterBlacker2 : clrs.black,
                                fontSize: "1.3rem",
                                cursor: isLeftAvgTypeDisabled() ? "default" : "pointer"
                            }}/>
                        <span style={{fontWeight: 700, width: 100, textAlign: "center"}}>
                            {avgType === 'MINUTE' && <IntlMessage id={'dataPage.minutes'}/>}
                            {avgType === 'HOUR' && <IntlMessage id={'dataPage.hours'}/>}
                        </span>
                        <RightOutlined
                            onClick={() => {
                                if (!isRightAvgTypeDisabled()) {
                                    setAvgType('HOUR')
                                }
                            }}
                            style={{
                                color: isRightAvgTypeDisabled() ? clrs.whiterBlacker2 : clrs.black,
                                fontSize: "1.3rem",
                                cursor: isRightAvgTypeDisabled() ? "default" : "pointer"
                            }}/>
                    </Flex>
                }

                <Tooltip title={<IntlMessage id={'dataPage.update'}/>}>
                    <Flex onClick={() => {
                        if (showAnalytics) {
                            handleGetTrendsAnalytics()
                        } else {
                            handleGetTrends()
                        }
                    }} gap={30} align={"center"} justify={"center"}
                          style={{
                              padding: 15,
                              backgroundColor: clrs.whiterOrange,
                              borderRadius: 10,
                              borderBottom: `3px solid ${clrs.whiterBlacker}`,
                              cursor: "pointer"
                          }}>
                        <SyncOutlined
                            style={{
                                fontSize: "1.3rem",
                                color: "white"
                            }}/>
                    </Flex>
                </Tooltip>

                {showAnalytics ?
                    <Flex onClick={() => {setShowAnalytics(false)}} gap={10} align={"center"} justify={"center"}
                          style={{
                              padding: 15,
                              backgroundColor: clrs.yellow2,
                              borderRadius: 10,
                              borderBottom: `3px solid ${clrs.whiterBlacker}`,
                              color: clrs.blacker,
                              cursor: "pointer"
                          }}>
                        <AppstoreFilled
                            style={{
                                fontSize: "1.3rem",
                            }}/>
                        <span style={{fontWeight: 700}}>
                            <IntlMessage id={'dataPage.table'}/>
                        </span>
                    </Flex>
                    :
                    <Flex onClick={() => {setShowAnalytics(true)}} gap={10} align={"center"} justify={"center"}
                          style={{
                              padding: 15,
                              backgroundColor: clrs.yellow2,
                              borderRadius: 10,
                              borderBottom: `3px solid ${clrs.whiterBlacker}`,
                              color: clrs.blacker,
                              cursor: "pointer"
                          }}>
                        <PieChartFilled
                            style={{
                                fontSize: "1.3rem",
                            }}/>
                        <span style={{fontWeight: 700}}>
                            <IntlMessage id={'dataPage.analytics'}/>
                        </span>
                    </Flex>
                }
            </Flex>

            {!showAnalytics ?
                <Flex style={{width: "100%", position: "relative", paddingBottom: 100}}>
                    <DataTable trends={trends} loading={loading}/>
                </Flex>
                :
                <Flex style={{width: "100%", position: "relative", paddingBottom: 100}} vertical gap={50}>
                    <BlockLoading isLoading={loadingAnalytics}/>
                    <AnalyticsView
                        trendsAnalytics={trendsAnalytics}
                        trendsAnalyticsMonth={trendsAnalyticsMonth}
                    />
                </Flex>
            }
        </>
    );
};

export default ArchiveView;